import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import {
  useStateWithDebounce,
  SortDirectionEnum,
  TestResult,
  TestType,
  getFormattedDateAndTime,
  useAuth,
  SortByType,
  GroupMemberRole,
  CompanyMemberRole,
  useGroupsData,
  TestsResultsAnalyticsClient,
  TestsResultsAnalyticsClientVariables,
  TestsResultsAnalyticsClient_testsResultsAnalyticsClient_tests,
} from '@modmd/data'
import { useLazyQuery } from '@apollo/client'
import { Container, Box } from 'components/Layout'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist from 'components/Datalist'
import TextInput from 'components/TextInput'
import { AnalyticsTestsResultsClientDatalist } from 'sharedComponents/AnalyticsTestsResultsClientDatalist'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { isInfluenza, useSortChangeHandler } from 'utils/helpers'
import Button from 'components/Button'
import { useDownloadableCsv } from 'utils/hooks/useGenerateDownloadableCsv'
import { RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import { TYPE_OPTIONS } from 'internal-portal/routes/TestSessions/utils'
import { TESTS_RESULTS_ANALYTICS_CLIENT } from './operations'
import { TestSessionPaymentStatus } from '@modmd/data'
import Icon from 'components/Icon'

interface RouterProps {
  companyId: string
}

export enum FormName {
  NAME_LIKE = 'nameLike',
}

export enum PaginationProps {
  PAGE = 'page',
}

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  memberLike?: string
  companyOrGroupLike?: string
  user?: string
  result?: TestResult | 'pending'
  companyId?: string
  groupId?: string
  barcode?: string
  type?: TestType
  resultsPerPage?: string
  typeFilter?: string
  paymentStatus?: TestSessionPaymentStatus
  insuranceCompany?: string
  patientName?: string
}

const Analytics: React.VFC<RouteComponentProps<RouterProps>> = ({ match }) => {
  const { companyId } = match.params

  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const searchState = useSearchState<FilterState>()
  const { sorter, handleSortChange } = useSortChangeHandler()
  const { getUserCompaniesWithRole, getUserGroupsWithRole, data: authData } = useAuth()
  const { hasGroupsWithRole: isGroupManager, groups: managerGroups } = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Manager],
  })

  const { hasCompaniesWithRole: isCompanyManagerOrOwner, companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })

  const { data: companyGroups } = useGroupsData({
    membersFilter: {
      isDeleted: false,
    },
    filter: {
      companyIds: companies.map(({ id }: { id: string }) => id),
      memberUserIds: [authData.User.id],
      memberUserGroupRoles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
    },
    membersPageLength: 1,
  })

  let groupsId = [] as String[] | undefined
  if (isCompanyManagerOrOwner) groupsId = companyGroups?.groups?.Groups?.map((group) => group.id)
  if (isGroupManager && !groupsId?.length) groupsId = managerGroups.map((group) => group.id)

  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)

  const [getAnalytics, { data: analyticsData, loading: isAnalyticsDataLoading }] = useLazyQuery<
    TestsResultsAnalyticsClient,
    TestsResultsAnalyticsClientVariables
  >(TESTS_RESULTS_ANALYTICS_CLIENT)
  const queryVariables = React.useMemo<TestsResultsAnalyticsClientVariables>(
    () => ({
      filterData: {
        testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
        type: searchState.data.type?.length ? searchState.data.type : undefined,
        groupIds: searchState.data.groupId?.length
          ? ([searchState.data.groupId] as string[])
          : (groupsId as string[]),
        companyIds: [companyId],
        result:
          searchState.data.result?.length && searchState.data.result !== 'pending'
            ? searchState.data.result
            : undefined,
        hasResult: searchState.data.result !== 'pending',
        typeFilter: searchState.data.typeFilter !== 'All' ? searchState.data.typeFilter : undefined,
        paymentStatus: !!searchState.data.paymentStatus
          ? searchState.data.paymentStatus
          : undefined,
        insuranceCompany: searchState.data.insuranceCompany || undefined,
        patientName: searchState.data.patientName || undefined,
      },
      pagination: {
        from:
          (Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value)) *
          ((Number(searchState.data.page) || 1) - 1),
        length: Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value),
        sortBy: sorter?.sortBy || SortByType.RESULT_RECIEVED_AT,
        direction: sorter?.direction || SortDirectionEnum.ASC,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchState.data, groupsId?.length]
  )

  useEffect(() => {
    const fetchData = () => {
      getAnalytics({ variables: queryVariables })
    }
    if (groupsId?.length || searchState.data.groupId?.length) {
      void fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.data, groupsId?.length])

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const csvTestsResults = React.useMemo(
    () =>
      analyticsData?.testsResultsAnalyticsClient.tests.map(
        (test: TestsResultsAnalyticsClient_testsResultsAnalyticsClient_tests) => {
          const { User, barcode, result, TestSession, secondResult, Insurance } = test
          const participant = User
          const patient = `${participant.firstName || ''} ${participant.lastName || ''}`
          const email = participant.email || ''
          const birthDate = getFormattedDateAndTime(participant.birthDate)
          const resultFull = isInfluenza(TestSession.type as TestType)
            ? `A: ${result ?? ''} | B: ${(secondResult as string) ?? ''}`
            : result
          const insuranceCompany = Insurance?.insuranceCompany || ''
          const testResult = {
            patient,
            email,
            birthDate,
            barcode,
            result: resultFull,
            testCategory: TestSession.type,
            testSessionName: TestSession.name,
            insuranceCompany,
          }
          return testResult
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(analyticsData?.testsResultsAnalyticsClient.tests)]
  )

  const csvUrl = useDownloadableCsv({ data: csvTestsResults })

  return (
    <CompanyPageLayout>
      <Container>
        <AnalyticsTestsResultsClientDatalist
          tests={analyticsData?.testsResultsAnalyticsClient.tests || []}
          pagination={analyticsData?.testsResultsAnalyticsClient.pagination}
          isLoading={isAnalyticsDataLoading}
          tableHeader={
            <>
              <Datalist.Title
                rightContent={
                  <Button
                    appearance="filled"
                    href={csvUrl}
                    download={`Tests-Results-${Date()}.csv`}
                  >
                    Download csv
                  </Button>
                }
              >
                Analytics
              </Datalist.Title>

              <Box
                display="grid"
                gridGap="1rem"
                gridTemplateColumns={
                  isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
                }
              >
                <DateInput
                  id="dateFrom"
                  name="dateFrom"
                  label="Date From"
                  value={filterData.dateFrom ? new Date(filterData.dateFrom) : lastWeek}
                  onChange={(date) => {
                    setFilterData({
                      ...filterData,
                      dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                    })
                  }}
                  hasBottomContent
                  isFullWidth
                />
                <DateInput
                  id="dateTo"
                  name="dateTo"
                  label="Date To"
                  value={filterData.dateTo ? new Date(filterData.dateTo) : today}
                  onChange={(date) => {
                    setFilterData({
                      ...filterData,
                      dateTo: date ? getFormattedDateAndTime(date) : undefined,
                    })
                  }}
                  hasBottomContent
                  isFullWidth
                />
                <SelectInput
                  name="type"
                  label="Type"
                  value={filterData.type || ''}
                  onChange={handleInputChange}
                  options={TYPE_OPTIONS}
                  hasBottomContent
                />
                <SelectInput
                  name="groupId"
                  label="Group"
                  value={filterData.groupId || ''}
                  onChange={handleInputChange}
                  options={
                    isCompanyManagerOrOwner && companyGroups?.groups?.Groups
                      ? [
                          { value: '', label: 'All' },
                          ...companyGroups?.groups?.Groups.map((group) => ({
                            value: group.id,
                            label: group.name,
                          })),
                        ]
                      : [
                          { value: '', label: 'All' },
                          ...managerGroups.map((group) => ({
                            value: group.id,
                            label: group.name,
                          })),
                        ]
                  }
                  hasBottomContent
                />
                <SelectInput
                  name="result"
                  label="Result"
                  value={filterData.result || ''}
                  onChange={handleInputChange}
                  options={[
                    { value: '', label: 'All' },
                    ...Object.values(TestResult).map((result) => ({
                      value: result,
                      label: result,
                    })),
                    { value: 'pending', label: 'Pending' },
                  ]}
                  hasBottomContent
                />
                <SelectInput
                  name="resultsPerPage"
                  label="Results per page"
                  value={filterData.resultsPerPage || ''}
                  onChange={handleInputChange}
                  options={RESULTS_PER_PAGE}
                  hasBottomContent
                />
                <SelectInput
                  name="typeFilter"
                  label="Type Filter"
                  value={filterData.typeFilter || ''}
                  onChange={handleInputChange}
                  options={[
                    {
                      value: 'All',
                      label: 'All',
                    },
                    {
                      value: 'TestSession',
                      label: 'Test',
                    },
                    {
                      value: 'Event',
                      label: 'Events',
                    },
                  ]}
                  hasBottomContent
                />
                <SelectInput
                  name="paymentStatus"
                  label="Payment"
                  value={filterData.paymentStatus || ''}
                  onChange={handleInputChange}
                  options={[
                    {
                      value: '',
                      label: 'All',
                    },
                    {
                      value: 'CreditCard',
                      label: 'Credit Card',
                    },
                    {
                      value: 'Invoice',
                      label: 'Invoice',
                    },
                    {
                      value: 'Insurance',
                      label: 'Insurance',
                    },
                  ]}
                  hasBottomContent
                />
                <TextInput
                  name="insuranceCompany"
                  label="Insurance Company"
                  value={filterData.insuranceCompany || ''}
                  onChange={handleInputChange}
                  leftIcon={<Icon.Search />}
                  placeholder="Search by insurance company"
                  hasBottomContent
                />
                <TextInput
                  name="patientName"
                  label="Patient Name"
                  value={filterData.patientName || ''}
                  onChange={handleInputChange}
                  leftIcon={<Icon.Search />}
                  placeholder="Search by patient name"
                  hasBottomContent
                />
              </Box>
            </>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={analyticsData?.testsResultsAnalyticsClient.pagination.length}
        />
      </Container>
    </CompanyPageLayout>
  )
}

export { Analytics }
